import React from 'react';
import { resolveGuestPaymentHeading } from './ownerFinancialPresentation.js';
import {
  resolveFinancialDiscountLineItems,
  sumFinancialDiscountLineItems,
} from '../../../utils/financialDiscounts.js';

/**
 * BookingFinancialFolio — Presentational financial folio (Owner Payout | Guest Payment)
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 6a: Extracted from BookingDetailModal.jsx. Pure presentational; no hooks, no effects, no API.
 *
 * Purpose:
 *   Render the two-column financial layout: Owner Payout (primary, 60%) and Guest Payment (reference, 40%).
 *   Labels, amounts, breakdown rows, and tooltips are received as computed props or passed-in nodes.
 *
 * Assumptions & Invariants:
 *   - No fetch; no hooks; no state. Parent owns all computations and passes props.
 *   - ownerPayoutLabelNode is the pre-rendered channel-specific label (VrboSection/ClcLeaseSection/etc.).
 *   - Same strings/labels/tooltips as before extraction; no behavior change.
 *
 * Dependencies: React only (JSX). Parent passes styles, formatters, and predicate refs.
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 6a: Extracted from BookingDetailModal.jsx (folio JSX moved verbatim).
 */

const VRBO_ICAL_TAX_TOOLTIP = "Prior to September 2025, VRBO paid Tourist Tax on the owner's behalf. For new bookings, Tourist Tax is now deposited to and paid by the owner.";
const VRBO_ICAL_CHANNEL_FEES_TOOLTIP = "Legacy VRBO iCal host fee (3%). This fee was charged by VRBO and is deducted from the payout.";
/** Legacy VRBO iCal: display-only. Payment service processing fees from Hostaway (not used in payout). */
const VRBO_ICAL_PAYMENT_SERVICE_PROCESSING_FEES_TOOLTIP = "Fee recorded by Hostaway for this legacy VRBO iCal reservation.";

/** Crissy: bookingengine Guest Payment. Guest-paid fee. */
const BOOKINGENGINE_GUEST_CHANNEL_FEE_TOOLTIP = "Paid by the guest as part of Direct Booking (Website) charges.";
/** Crissy: bookingengine Guest Payment. Stripe processing fees = Channel Fee, owner-borne. */
const BOOKINGENGINE_CHANNEL_FEE_STRIPE_TOOLTIP = "Stripe processing fees paid by owner. CLC has applied markup to base rate to offset these fees";

/** CLC–Lease Owner Payout: amount collected (not owner earnings). */
const LEASE_TOURIST_TAX_COLLECTED_TOOLTIP = "Collected from the guest for tax remittance (not owner earnings).";

/** Info link URL for all Channel Fees tooltips (Owner Financial Flow PDF). */
const CHANNEL_FEES_INFO_URL = 'https://www.dropbox.com/scl/fo/wulsbqkj77x923xgll1vq/ANkBmN_Xq2Csrq0JMFRZa9Q/To%20Use/Owner%20Financial%20Flow%20.pdf?rlkey=xb3uks5p246lmkhewb4ji4kv9&e=2&dl=0';

/**
 * Wraps Channel Fees tooltip text with existing copy plus "Info" link below (all channels).
 * @param {string} text - Existing tooltip copy (unchanged).
 * @returns {React.ReactNode} - Tooltip content: text + newline + Info link (opens in new tab).
 */
function channelFeesTooltipWithInfoLink(text) {
  return (
    <div>
      <div>{text}</div>
      <div style={{ marginTop: '6px', fontSize: '11px' }}>
        <a href={CHANNEL_FEES_INFO_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Info</a>
      </div>
    </div>
  );
}

/** Small badge for Owner/Guest on channel fee rows (no channel-specific text). */
function ChannelFeeBadge({ who, theme }) {
  const isOwner = who === 'owner';
  const bg = theme === 'dark' ? (isOwner ? 'rgba(80,120,200,0.2)' : 'rgba(100,160,100,0.2)') : (isOwner ? 'rgba(80,120,200,0.12)' : 'rgba(100,160,100,0.12)');
  const color = theme === 'dark' ? (isOwner ? '#8ab4f8' : '#81c995') : (isOwner ? '#1a73e8' : '#0d652d');
  return (
    <span style={{
      fontSize: '10px',
      fontWeight: 600,
      padding: '2px 6px',
      borderRadius: '4px',
      backgroundColor: bg,
      color,
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
    }}>
      {isOwner ? 'Owner' : 'Guest'}
    </span>
  );
}

export default function BookingFinancialFolio({
  theme, styles, hostPayout, guestPaid, channelFeeLineItems = [], detailData, booking, persistedSummary,
  isLegacyVRBOiCal = false,
  ownerPayoutLabelNode,
  handleOwnerPayoutTooltip, hideTooltip, showTooltip,
  formatCurrencyRefined, formatFinanceValue, formatDateOnlyET,
  resolveCanonical, checkVRBONonICalComputed, checkCLCLease, checkBookingEngine, checkLeaseStrict, checkCLC,
}) {
  const ownerDiscountLineItems = resolveFinancialDiscountLineItems({
    discountLineItems: hostPayout?.discountLineItems,
    discountsTotal: hostPayout?.discountsTotal,
    legacyCouponDiscount: hostPayout?.couponDiscount,
    legacyWeeklyDiscount: hostPayout?.weeklyDiscount,
  });
  const guestDiscountLineItems = resolveFinancialDiscountLineItems({
    discountLineItems: guestPaid?.discountLineItems,
    discountsTotal: guestPaid?.discountsTotal,
    legacyCouponDiscount: guestPaid?.couponDiscount,
    legacyWeeklyDiscount: guestPaid?.weeklyDiscount,
  });
  const guestDiscountsTotal = sumFinancialDiscountLineItems(guestDiscountLineItems);

  return (
  <div data-bookingdetail-inline-folio style={styles.folioTwoColumnLayout}>
  {/* LEFT COLUMN: OWNER PAYOUT (PRIMARY, 60%) */}
  <div style={styles.folioColumnOwner}>
    {['unresolved', 'source_unavailable'].includes(persistedSummary?.owner_payout?.owner_discount_status) && (
      <p role="status">Discount details need review. Net base rate is unavailable.</p>
    )}
    {/* Section Title: Owner Payout (Semantic Anchor) */}
    <div style={{
      ...styles.folioColumnTitle,
      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <span>Owner Payout</span>
    </div>
    
    {/* OWNER PAYOUT STATEMENT LOCKUP: Statement Moment (Critical) */}
    <div style={styles.folioColumnLockup}>
      {/* Line 0: "Paid to Owner" label with tooltip (only label above hero amount) */}
      <div style={{
        fontSize: '14px',
        fontWeight: 'var(--font-weight-normal)',
        color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'wrap',
      }}>
      {ownerPayoutLabelNode}
      {/* INTENT: Phase 4a — Single helper getOwnerPayoutTooltipText used by both handlers; no duplicated branching. */}
        <span
          style={{ ...styles.folioInfoIcon, pointerEvents: 'auto', flexShrink: 0 }}
          onMouseEnter={handleOwnerPayoutTooltip}
          onMouseLeave={hideTooltip}
          onFocus={handleOwnerPayoutTooltip}
          onBlur={hideTooltip}
          tabIndex={0}
          role="button"
          aria-label="Owner payout explanation"
        >
          ℹ️
        </span>
      </div>
      {/* Line 2: Amount (ALWAYS RENDER - Isolated, Largest on Page) */}
      <div style={{
        ...styles.folioColumnLockupAmountStatement,
        color: hostPayout.estimatedPayout !== null 
          ? (theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)')
          : (theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)'),
        opacity: hostPayout.estimatedPayout !== null ? 1 : 0.6, // Muted when missing
      }}>
        {hostPayout.estimatedPayout !== null 
          ? formatCurrencyRefined(hostPayout.estimatedPayout)
          : (hostPayout.payoutStatus === 'unknown_missing_hostaway' ? 'Unknown' : '—')
        }
      </div>
      {/* VRBO non-iCal computed: "Host payout so far" removed per Crissy's requirements - not shown in Owner Portal */}
      {/* Line 3: Timing Cue (Italic, Subtle) - Only show when amount exists AND copy_mode='provisional' */}
      {hostPayout.estimatedPayout !== null && (hostPayout.isProvisionalCopy !== false && hostPayout.copyMode !== 'final') && hostPayout.estimatedPayoutDate && (
        <div style={{
          ...styles.folioColumnLockupTiming,
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          fontStyle: 'italic',
          opacity: 0.7,
        }}>
          {(() => {
            // For "deposited" status, show deposit date (or nothing)
            if (hostPayout.payoutStatus === 'deposited') {
              if (hostPayout.estimatedPayoutDate) {
                const date = new Date(hostPayout.estimatedPayoutDate);
                const formattedDate = formatDateOnlyET(date.toISOString().split('T')[0], { long: true });
                return `Deposited: ${formattedDate}`;
              }
              return 'Deposited'; // Just show status if no date
            }
            // For "estimated" or "pending" status, show estimated payout date
            if (hostPayout.estimatedPayoutDate) {
              const date = new Date(hostPayout.estimatedPayoutDate);
              const formattedDate = formatDateOnlyET(date.toISOString().split('T')[0], { long: true });
              return `Estimated payout date: ${formattedDate}`;
            }
            return 'Expected payout from channel after check-out';
          })()}
        </div>
      )}
      {/* For final mode (past reservations), no payout date line is shown */}
      {/* Line 3 (when pending): Status message - only show if no payout amount AND provisional mode */}
      {hostPayout.estimatedPayout === null && hostPayout.payoutStatus !== 'deposited' && (hostPayout.isProvisionalCopy !== false && hostPayout.copyMode !== 'final') && (
        <div style={{
          ...styles.folioColumnLockupTiming,
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
        }}>
          Pending from channel
        </div>
      )}
    </div>
    
    {/* Breakdown */}
    {(() => {
      const canonicalOwner = resolveCanonical(detailData, booking);
      const isBEOwner = checkBookingEngine(canonicalOwner);
      const pfOwner = persistedSummary?.processing_fees;
      const hpfOwner = pfOwner?.hostaway_payout_processing_fees;
      const beStripeFee = (hpfOwner != null && hpfOwner > 0) ? hpfOwner : parseFloat(pfOwner?.total_processing_fees || 0);
      const showBEOwnerCF = isBEOwner && pfOwner && beStripeFee > 0;
      const isVrbo = canonicalOwner?.master_channel_key === 'vrbo';
      const ownerCfNull = hostPayout.channelFees === null || hostPayout.channelFees === undefined;
      // Owner-side channel fee rows: from normalized list; fallback one row when list empty but we have amount (backward compat)
      let ownerSideChannelFeeItems = (channelFeeLineItems || []).filter((i) => i.borneBy === 'owner');
      if (ownerSideChannelFeeItems.length === 0 && (hostPayout.channelFees != null || showBEOwnerCF)) {
        ownerSideChannelFeeItems = [{ amount: showBEOwnerCF ? -beStripeFee : hostPayout.channelFees, borneBy: 'owner', labelKey: 'channel_fees' }];
      }
      const showOwnerCF = ownerSideChannelFeeItems.length > 0;
      
      // Payment Service Processing Fees (PSPF): parse from processing_fees
      const pspfRaw = persistedSummary?.processing_fees?.hostaway_payout_processing_fees;
      const pspfNum = pspfRaw != null ? (typeof pspfRaw === 'number' ? pspfRaw : parseFloat(pspfRaw)) : null;
      const pspfExists = Number.isFinite(pspfNum) && pspfNum !== 0;
      
      // Channel Fees amount (for dedupe comparison): use absolute value
      const channelFeesNum = showBEOwnerCF 
        ? Math.abs(beStripeFee) 
        : (hostPayout.channelFees != null ? Math.abs(hostPayout.channelFees) : null);
      
      // DEDUPE RULE (all channels): If both Channel Fees and PSPF would render and amounts match within $0.01, suppress PSPF row.
      const bothWouldRender = showOwnerCF && pspfExists;
      const amountsMatch = bothWouldRender && channelFeesNum != null && Math.abs(Math.abs(pspfNum) - channelFeesNum) <= 0.01;
      const showPspfRow = !isLegacyVRBOiCal && pspfExists && !amountsMatch;
      const showLegacyProcessingReference = isLegacyVRBOiCal && pspfExists && !amountsMatch;
      const pspfDisplay = showPspfRow ? (pspfNum < 0 ? pspfNum : -Math.abs(pspfNum)) : null;
      const isClcLease = checkCLCLease(canonicalOwner);
      const _tt = guestPaid?.touristTax != null && guestPaid.touristTax !== '' ? parseFloat(guestPaid.touristTax) : NaN;
      const leaseTouristTax = Number.isFinite(_tt) ? _tt : null;
      const _dd = (guestPaid?.damageDepositAmount != null && guestPaid.damageDepositAmount !== '') ? parseFloat(guestPaid.damageDepositAmount) : NaN;
      const leaseDamageDeposit = Number.isFinite(_dd) ? _dd : 0;
      const showLeaseCollected = isClcLease && (leaseTouristTax != null || leaseDamageDeposit > 0);
      const hasOwnerDiscounts = ownerDiscountLineItems.length > 0;
      const showBreakdown = hostPayout.baseRate != null || hostPayout.cleaningFee != null || showOwnerCF || (hostPayout.adjustments != null && hostPayout.adjustments !== 0) || showPspfRow || showLegacyProcessingReference || showLeaseCollected || hasOwnerDiscounts;
      if (!showBreakdown) return null;
      return (
      <>
        <div style={{
          ...styles.folioColumnDividerThick,
          backgroundColor: theme === 'dark' 
            ? 'rgba(255, 255, 255, 0.12)' 
            : 'rgba(200, 195, 188, 0.25)', // Thicker rule above breakdown
          marginTop: '0', // Lockup already has breathing room (24px)
          marginBottom: '16px', // Increased for breathing room
        }} />
        <div style={{
          ...styles.folioColumnBreakdownTitle,
          color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
        }}>
          Breakdown
        </div>
        <div style={styles.folioColumnLedger}>
          {hostPayout.baseRate !== null && (
            <>
              <div style={styles.folioColumnLedgerRow}>
                <span style={{
                  ...styles.folioColumnLedgerLabel,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>Base Rate</span>
                <span style={{
                  ...styles.folioColumnLedgerValue,
                  color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                }}>
                  {formatFinanceValue(hostPayout.baseRate)}
                </span>
              </div>
              <div style={{
                ...styles.folioColumnDivider,
                backgroundColor: theme === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)' 
                  : 'rgba(200, 195, 188, 0.15)',
              }} />
            </>
          )}
          {ownerDiscountLineItems.map((item) => (
            <React.Fragment key={`owner-discount-${item.key}`}>
              <div style={styles.folioColumnLedgerRow}>
                <span style={{
                  ...styles.folioColumnLedgerLabel,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>{item.label}</span>
                <span style={{
                  ...styles.folioColumnLedgerValue,
                  color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                }}>
                  {formatFinanceValue(item.amount)}
                </span>
              </div>
              <div style={{
                ...styles.folioColumnDivider,
                backgroundColor: theme === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)' 
                  : 'rgba(200, 195, 188, 0.15)',
              }} />
            </React.Fragment>
          ))}
          {hostPayout.cleaningFee !== null && (
            <>
              <div style={styles.folioColumnLedgerRow}>
                <span style={{
                  ...styles.folioColumnLedgerLabel,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>Cleaning Fee</span>
                <span style={{
                  ...styles.folioColumnLedgerValue,
                  color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                }}>
                  {formatFinanceValue(hostPayout.cleaningFee)}
                </span>
              </div>
              <div style={{
                ...styles.folioColumnDivider,
                backgroundColor: theme === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)' 
                  : 'rgba(200, 195, 188, 0.15)',
              }} />
            </>
          )}
          {/* CLC–Lease amount collected: Tourist Tax + Damage Deposit (informational, not owner earnings) */}
          {showLeaseCollected && (
            <>
              {isClcLease && leaseTouristTax != null && (
                <div style={styles.folioColumnLedgerRow}>
                  <span style={{
                    ...styles.folioColumnLedgerLabel,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                  }}>
                    Tourist Tax
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 'var(--font-weight-normal)',
                      color: theme === 'dark' ? 'var(--color-text-tertiary-dark)' : 'var(--color-text-tertiary)',
                      fontStyle: 'italic',
                      padding: '2px 6px',
                      backgroundColor: theme === 'dark' ? 'rgba(131, 189, 193, 0.15)' : 'rgba(131, 189, 193, 0.1)',
                      borderRadius: '4px',
                      marginLeft: '2px',
                    }}>
                      Owner remits
                    </span>
                    <span
                      style={styles.folioInfoIcon}
                      onMouseEnter={(e) => showTooltip(e, LEASE_TOURIST_TAX_COLLECTED_TOOLTIP)}
                      onMouseLeave={hideTooltip}
                      onFocus={(e) => showTooltip(e, LEASE_TOURIST_TAX_COLLECTED_TOOLTIP)}
                      onBlur={hideTooltip}
                      tabIndex={0}
                      role="button"
                      aria-label="Tourist tax collected explanation"
                    >
                      ℹ️
                    </span>
                  </span>
                  <span style={{
                    ...styles.folioColumnLedgerValue,
                    color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                  }}>
                    {formatFinanceValue(leaseTouristTax)}
                  </span>
                </div>
              )}
              {isClcLease && leaseDamageDeposit > 0 && (
                <>
                  <div style={styles.folioColumnLedgerRow}>
                    <span style={{
                      ...styles.folioColumnLedgerLabel,
                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                    }}>
                      Damage Deposit
                    </span>
                    <span style={{
                      ...styles.folioColumnLedgerValue,
                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                    }}>
                      {formatFinanceValue(leaseDamageDeposit)}
                    </span>
                  </div>
                  <div className="damageDepositHeldInTrustNote">
                    Held in trust and refunded within 14 days of checkout, minus any damages.
                  </div>
                </>
              )}
              {showLeaseCollected && (
                <div style={{
                  ...styles.folioColumnDivider,
                  backgroundColor: theme === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(200, 195, 188, 0.15)',
                }} />
              )}
            </>
          )}
          {/* Tourist Tax for VRBO non-iCal and Lease (owner-paid/owner-remits) */}
          {(() => {
            const canonical = resolveCanonical(detailData, booking);
            const isVRBONonICalComputed = checkVRBONonICalComputed(canonical);
            const isLease = checkCLCLease(canonical);
            const touristTax = isLease ? hostPayout?.ownerTouristTax : guestPaid?.touristTax;
            if ((isVRBONonICalComputed || isLease) && touristTax !== null && touristTax !== undefined) {
              return (
                <>
                  <div style={styles.folioColumnLedgerRow}>
                    <span style={{
                      ...styles.folioColumnLedgerLabel,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                    }}>
                      Tourist Tax
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 'var(--font-weight-normal)',
                        color: theme === 'dark' ? 'var(--color-text-tertiary-dark)' : 'var(--color-text-tertiary)',
                        fontStyle: 'italic',
                        padding: '2px 6px',
                        backgroundColor: theme === 'dark' ? 'rgba(131, 189, 193, 0.15)' : 'rgba(131, 189, 193, 0.1)',
                        borderRadius: '4px',
                        marginLeft: '4px',
                      }}>
                        Owner remits
                      </span>
                      <span
                        style={styles.folioInfoIcon}
                        onMouseEnter={(e) => {
                          const canonical = resolveCanonical(detailData, booking);
                          const masterChannelKey = canonical?.master_channel_key;
                          const tooltipText = (masterChannelKey === 'vrbo' || masterChannelKey === 'clc')
                            ? 'Taxes collected and remitted by Owner'
                            : 'Tourist tax is collected from the guest and must be remitted by the owner to the local tax authority.';
                          showTooltip(e, tooltipText);
                        }}
                        onMouseLeave={hideTooltip}
                        onFocus={(e) => {
                          const canonical = resolveCanonical(detailData, booking);
                          const masterChannelKey = canonical?.master_channel_key;
                          const tooltipText = (masterChannelKey === 'vrbo' || masterChannelKey === 'clc')
                            ? 'Taxes collected and remitted by Owner'
                            : 'Tourist tax is collected from the guest and must be remitted by the owner to the local tax authority.';
                          showTooltip(e, tooltipText);
                        }}
                        onBlur={hideTooltip}
                        tabIndex={0}
                        role="button"
                        aria-label="Tourist tax owner remits explanation"
                      >
                        ℹ️
                      </span>
                    </span>
                    <span style={{
                      ...styles.folioColumnLedgerValue,
                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                    }}>
                      {formatFinanceValue(touristTax)}
                    </span>
                  </div>
                  <div style={{
                    ...styles.folioColumnDivider,
                    backgroundColor: theme === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(200, 195, 188, 0.15)',
                  }} />
                </>
              );
            }
            return null;
          })()}
          {/* Tourist Tax for CLC / Bookingengine (owner-paid/owner-remits) */}
          {/* NOTE: Lease is handled in the VRBO non-iCal and Lease block above to avoid duplication */}
          {(() => {
            const canonical = resolveCanonical(detailData, booking);
            const isBookingEngine = checkBookingEngine(canonical);
            const isCLC = canonical?.master_channel_key === 'clc';
            const isClcLease = checkCLCLease(canonical);
            const isClcNonLease = isCLC && !isClcLease;
            // Use tourist_tax_to_subtract from persisted summary (not hostPayout.ownerTouristTax which is null)
            const ownerTouristTax = persistedSummary?.tourist_tax?.tourist_tax_to_subtract !== undefined
              ? parseFloat(persistedSummary.tourist_tax.tourist_tax_to_subtract || 0)
              : null;
            const ownerRemits = persistedSummary?.tourist_tax?.owner_remits;
            if ((isBookingEngine || isClcNonLease) && ownerRemits && ownerTouristTax !== null && ownerTouristTax !== undefined && ownerTouristTax > 0) {
              return (
                <>
                  <div style={styles.folioColumnLedgerRow}>
                    <span style={{
                      ...styles.folioColumnLedgerLabel,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                    }}>
                      Tourist Tax
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 'var(--font-weight-normal)',
                        color: theme === 'dark' ? 'var(--color-text-tertiary-dark)' : 'var(--color-text-tertiary)',
                        fontStyle: 'italic',
                        padding: '2px 6px',
                        backgroundColor: theme === 'dark' ? 'rgba(131, 189, 193, 0.15)' : 'rgba(131, 189, 193, 0.1)',
                        borderRadius: '4px',
                        marginLeft: '4px',
                      }}>
                        Owner remits
                      </span>
                      <span
                        style={styles.folioInfoIcon}
                        onMouseEnter={(e) => {
                          showTooltip(e, 'Taxes collected and remitted by Owner');
                        }}
                        onMouseLeave={hideTooltip}
                        onFocus={(e) => {
                          showTooltip(e, 'Taxes collected and remitted by Owner');
                        }}
                        onBlur={hideTooltip}
                        tabIndex={0}
                        role="button"
                        aria-label="Tourist tax owner remits explanation"
                      >
                        ℹ️
                      </span>
                    </span>
                    <span style={{
                      ...styles.folioColumnLedgerValue,
                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                    }}>
                      {formatFinanceValue(ownerTouristTax)}
                    </span>
                  </div>
                  <div style={{
                    ...styles.folioColumnDivider,
                    backgroundColor: theme === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(200, 195, 188, 0.15)',
                  }} />
                </>
              );
            }
            return null;
          })()}
          {/* Security/Damage Deposit for CLC-Lease and Bookingengine (held in trust) */}
          {(() => {
            const canonical = resolveCanonical(detailData, booking);
            const isLease = checkLeaseStrict(canonical);
            const isBookingEngine = checkBookingEngine(canonical);
            const damageDeposit = hostPayout?.ownerDamageDeposit;
            // Only show deposit row if value > 0 (for both bookingengine and Lease)
            // Security Deposit must ONLY render if the numeric value > 0
            const shouldShowDeposit = (isBookingEngine || isLease) && damageDeposit !== null && damageDeposit !== undefined && parseFloat(damageDeposit) > 0;
            if (shouldShowDeposit) {
              // Use "Security Deposit" label for bookingengine, "Damage deposit" for Lease
              const depositLabel = isBookingEngine ? 'Security Deposit (held in trust)' : 'Damage deposit (held in trust)';
              // Use appropriate tooltip text based on whether amount is available
              const hasDepositAmount = damageDeposit !== null && damageDeposit !== undefined && damageDeposit > 0;
              const depositTooltip = isBookingEngine 
                ? (hasDepositAmount 
                  ? 'Security deposit is held in trust and refunded within 14 days after checkout.'
                  : 'Security deposit (if collected) is held in trust and refunded within 14 days after checkout. Amount may be unavailable from the channel.')
                : 'Damage deposit is held in trust and returned within 14 days of checkout, minus any damages.';
              // Display amount if available, otherwise show placeholder
              const displayAmount = hasDepositAmount ? damageDeposit : null;
              return (
                <>
                  <div style={styles.folioColumnLedgerRow}>
                    <span style={{
                      ...styles.folioColumnLedgerLabel,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                    }}>
                      {depositLabel}
                      <span
                        style={styles.folioInfoIcon}
                        onMouseEnter={(e) => {
                          showTooltip(e, depositTooltip);
                        }}
                        onMouseLeave={hideTooltip}
                        onFocus={(e) => {
                          showTooltip(e, depositTooltip);
                        }}
                        onBlur={hideTooltip}
                        tabIndex={0}
                        role="button"
                        aria-label="Security/damage deposit held in trust explanation"
                      >
                        ℹ️
                      </span>
                    </span>
                    <span style={{
                      ...styles.folioColumnLedgerValue,
                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                    }}>
                      {displayAmount !== null ? formatFinanceValue(displayAmount) : '—'}
                    </span>
                  </div>
                  <div style={{
                    ...styles.folioColumnDivider,
                    backgroundColor: theme === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(200, 195, 188, 0.15)',
                  }} />
                </>
              );
            }
            return null;
          })()}
          {/* INVARIANT A: Adjustments row only when non-zero; host channel fee is never in Adjustments */}
          {hostPayout.adjustmentsTotal != null && Math.abs(Number(hostPayout.adjustmentsTotal)) > 0.005 && (
            <>
              <div style={styles.folioColumnLedgerRow}>
                <span style={{
                  ...styles.folioColumnLedgerLabel,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  Adjustments
                  {hostPayout.adjustmentsBreakdown && hostPayout.adjustmentsBreakdown.length > 0 && (
                    <span
                      style={{ ...styles.folioInfoIcon, pointerEvents: 'auto', flexShrink: 0 }}
                      onMouseEnter={(e) => {
                        // Build tooltip with breakdown
                        const breakdownLines = hostPayout.adjustmentsBreakdown.map(item => {
                          const sign = item.amount >= 0 ? '+' : '';
                          return `${item.label}: ${sign}${formatFinanceValue(item.amount)}`;
                        });
                        const tooltipText = breakdownLines.join('\n');
                        showTooltip(e, tooltipText);
                      }}
                      onMouseLeave={hideTooltip}
                      onFocus={(e) => {
                        const breakdownLines = hostPayout.adjustmentsBreakdown.map(item => {
                          const sign = item.amount >= 0 ? '+' : '';
                          return `${item.label}: ${sign}${formatFinanceValue(item.amount)}`;
                        });
                        const tooltipText = breakdownLines.join('\n');
                        showTooltip(e, tooltipText);
                      }}
                      onBlur={hideTooltip}
                      tabIndex={0}
                      role="button"
                      aria-label="Adjustments breakdown explanation"
                    >
                      ℹ️
                    </span>
                  )}
                </span>
                <span style={{
                  ...styles.folioColumnLedgerValue,
                  color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                }}>
                  {formatFinanceValue(hostPayout.adjustmentsTotal)}
                </span>
              </div>
              <div style={{
                ...styles.folioColumnDivider,
                backgroundColor: theme === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)' 
                  : 'rgba(200, 195, 188, 0.15)',
              }} />
            </>
          )}
          {showOwnerCF && ownerSideChannelFeeItems.map((item, idx) => (
            <React.Fragment key={`owner-cf-${idx}`}>
              <div style={{
                ...styles.folioColumnDivider,
                backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(200, 195, 188, 0.15)',
                marginTop: idx === 0 ? '4px' : 0,
                marginBottom: '4px',
              }} />
              <div style={styles.folioColumnLedgerRow}>
                <span style={{
                  ...styles.folioColumnLedgerLabel,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  Channel Fees
                  <ChannelFeeBadge who="owner" theme={theme} />
                  <span
                    style={styles.folioInfoIcon}
                    onMouseEnter={(e) => {
                      const tooltipText = ownerCfNull && !showBEOwnerCF
                        ? 'Channel fees not provided for this reservation.'
                        : showBEOwnerCF
                          ? BOOKINGENGINE_CHANNEL_FEE_STRIPE_TOOLTIP
                          : (isLegacyVRBOiCal
                              ? VRBO_ICAL_CHANNEL_FEES_TOOLTIP
                              : (() => {
                                  const canonical = resolveCanonical(detailData, booking);
                                  const masterChannelKey = canonical?.master_channel_key;
                                  return masterChannelKey === 'airbnb'
                                    ? 'Prior to Nov 15, hosts were charged 3%. Airbnb now charges 15.5%. CLC applies a 15.5% markup to offset this change.'
                                    : masterChannelKey === 'vrbo'
                                      ? 'Estimated processing fees. Final fees update once all guest payments are completed (30 days prior to check in).'
                                      : 'A markup has been applied to the base rate to offset channel fees. Detailed videos will be available soon to explain this process.';
                                })());
                      showTooltip(e, ownerCfNull && !showBEOwnerCF ? tooltipText : channelFeesTooltipWithInfoLink(tooltipText));
                    }}
                    onMouseLeave={hideTooltip}
                    onFocus={(e) => {
                      const tooltipText = ownerCfNull && !showBEOwnerCF
                        ? 'Channel fees not provided for this reservation.'
                        : showBEOwnerCF
                          ? BOOKINGENGINE_CHANNEL_FEE_STRIPE_TOOLTIP
                          : (isLegacyVRBOiCal
                              ? VRBO_ICAL_CHANNEL_FEES_TOOLTIP
                              : (() => {
                                  const canonical = resolveCanonical(detailData, booking);
                                  const masterChannelKey = canonical?.master_channel_key;
                                  return masterChannelKey === 'airbnb'
                                    ? 'Prior to Nov 15, hosts were charged 3%. Airbnb now charges 15.5%. CLC applies a 15.5% markup to offset this change.'
                                    : masterChannelKey === 'vrbo'
                                      ? 'Estimated processing fees. Final fees update once all guest payments are completed (30 days prior to check in).'
                                      : 'A markup has been applied to the base rate to offset channel fees. Detailed videos will be available soon to explain this process.';
                                })());
                      showTooltip(e, ownerCfNull && !showBEOwnerCF ? tooltipText : channelFeesTooltipWithInfoLink(tooltipText));
                    }}
                    onBlur={hideTooltip}
                    tabIndex={0}
                    role="button"
                    aria-label="Channel fees explanation"
                  >
                    ℹ️
                  </span>
                </span>
                <span style={{
                  ...styles.folioColumnLedgerValue,
                  color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                }}>
                  {ownerCfNull && !showBEOwnerCF && idx === 0
                    ? 'Not available'
                    : (item.amount < 0 ? formatFinanceValue(item.amount) : formatFinanceValue(-Math.abs(item.amount)))}
                </span>
              </div>
            </React.Fragment>
          ))}
          {showPspfRow && (
              <>
                <div style={{
                  ...styles.folioColumnDivider,
                  backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(200, 195, 188, 0.15)',
                  marginTop: '4px',
                  marginBottom: '4px',
                }} />
                <div style={styles.folioColumnLedgerRow}>
                  <span style={{
                    ...styles.folioColumnLedgerLabel,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                  }}>
                    Payment Service Processing Fees
                    <span
                      style={styles.folioInfoIcon}
                      onMouseEnter={(e) => { showTooltip(e, VRBO_ICAL_PAYMENT_SERVICE_PROCESSING_FEES_TOOLTIP); }}
                      onMouseLeave={hideTooltip}
                      onFocus={(e) => { showTooltip(e, VRBO_ICAL_PAYMENT_SERVICE_PROCESSING_FEES_TOOLTIP); }}
                      onBlur={hideTooltip}
                      tabIndex={0}
                      role="button"
                      aria-label="Payment service processing fees explanation"
                    >
                      ℹ️
                    </span>
                  </span>
                  <span style={{
                    ...styles.folioColumnLedgerValue,
                    color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                  }}>
                    {formatFinanceValue(pspfDisplay)}
                  </span>
                </div>
              </>
          )}
        </div>
        {showLegacyProcessingReference && (
          <details style={{ marginTop: '8px', fontSize: '12px' }}>
            <summary style={{ cursor: 'pointer' }}>Processing fee reference</summary>
            <p style={{ margin: '6px 0 0' }}>
              Recorded processing fee: {formatFinanceValue(Math.abs(pspfNum))}.
              {' '}Reference only; this does not reduce the owner payout shown above.
            </p>
          </details>
        )}
      </>
      );
    })()}
  </div>

  {/* COLUMN DIVIDER */}
  <div style={{
    ...styles.folioColumnSeparator,
    backgroundColor: theme === 'dark' 
      ? 'rgba(255, 255, 255, 0.08)' 
      : 'rgba(200, 195, 188, 0.15)',
  }} />

  {/* RIGHT COLUMN: GUEST PAYMENT (REFERENCE, 40%) - Bottom-aligned with Owner breakdown */}
  <div style={{
    ...styles.folioColumnGuest,
    display: 'flex',
    flexDirection: 'column',
    height: '100%', // Full height from grid stretch
    // No justifyContent - content block uses marginTop: auto for bottom anchoring
  }}>
    {/* Bottom block: "Guest Payment" section title + primary line "Total Guest Payment" + Breakdown (entire section bottom-aligned) */}
    <div style={{ marginTop: 'auto' }}>
      {/* Section Title: "Guest Payment" (nightly text only under Base Rate) */}
      <div style={{
        ...styles.folioColumnTitleSecondary,
        color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
        marginBottom: '12px', // Natural spacing before total
      }}>
        Guest Payment
      </div>
      {/* Primary Line: "Total Guest Payment" or VRBO partial-paid "Paid to Date" + "Total Reservation Amount" */}
      {(() => {
        const canonical = resolveCanonical(detailData, booking);
        const isVrbo = persistedSummary?.channel?.master_channel_key === 'vrbo' || canonical?.master_channel_key === 'vrbo';
        const guestHeading = resolveGuestPaymentHeading({
          guestPaid, guestDiscountsTotal, isVrbo, isLegacyVRBOiCal,
        });
        if (guestPaid.totalCollected == null) return null;
        return (
          <>
            <div style={styles.folioColumnHeroRow}>
              <span style={{
                ...styles.folioColumnHeroLabelSecondary,
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
              }}>{guestHeading.label}</span>
              <span style={{
                ...styles.folioColumnHeroAmountSecondary,
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
              }}>
                {formatFinanceValue(guestPaid.totalCollected)}
              </span>
            </div>
            {guestHeading.showReservationAmount && (
              <div style={{
                ...styles.folioColumnHeroRow,
                marginTop: '6px',
                fontSize: '15px',
                fontWeight: 'var(--font-weight-medium)',
                opacity: 0.95,
                flexWrap: 'wrap',
                gap: '4px 12px',
              }}>
                <span style={{
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                  fontWeight: 'var(--font-weight-medium)',
                }}>Total Reservation Amount</span>
                <span style={{
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                  fontWeight: 'var(--font-weight-medium)',
                }}>
                  {formatFinanceValue(guestHeading.reservationAmount)}
                </span>
              </div>
            )}
          </>
        );
      })()}
    
    {/* Breakdown */}
    {(() => {
      // Check if we have any breakdown items to show
      const hasBaseRate = guestPaid.baseRate !== null;
      const hasCleaningFee = guestPaid.cleaningFee !== null;
      const hasTouristTax = guestPaid.touristTax !== null;
      // State/county only in Tourist Tax tooltip (no separate rows)
      const hasStateTaxInTooltip = guestPaid.stateTaxAmount != null && Math.abs(parseFloat(guestPaid.stateTaxAmount) || 0) > 0.005;
      const hasCountyTaxInTooltip = guestPaid.countyTaxAmount != null && Math.abs(parseFloat(guestPaid.countyTaxAmount) || 0) > 0.005;
      const hasGuestServiceFee = guestPaid.guestServiceFee !== null && parseFloat(guestPaid.guestServiceFee || 0) > 0;
      const hasGuestChannelFee = guestPaid.guestChannelFee !== null && parseFloat(guestPaid.guestChannelFee || 0) > 0;
      const hasGuestDiscounts = guestDiscountLineItems.length > 0;
      // Guest-side channel fee rows: from normalized list; fallback one row when list empty but we have amount (backward compat)
      let guestSideChannelFeeItems = (channelFeeLineItems || []).filter((i) => i.borneBy === 'guest');
      if (guestSideChannelFeeItems.length === 0 && hasGuestChannelFee) {
        guestSideChannelFeeItems = [{ amount: parseFloat(guestPaid.guestChannelFee), borneBy: 'guest', labelKey: 'channel_fees' }];
      }
      // Also check for processing fees (for bookingengine/CLC)
      const canonical = resolveCanonical(detailData, booking);
      const persistedChannel = persistedSummary?.channel;
      // Detect Airbnb: For Airbnb, guest_channel_fee should be shown as "Guest Service Fee"
      const isAirbnb = persistedChannel?.master_channel_key === 'airbnb' ||
                       canonical?.master_channel_key === 'airbnb' ||
                       (booking?.channel && typeof booking.channel === 'string' && booking.channel.toLowerCase().includes('airbnb'));
      // Airbnb de-dupe: Guest Service Fee and Channel Fees (GUEST) are the same amount; show only Guest Service Fee.
      if (isAirbnb && hasGuestChannelFee) {
        const chAmount = parseFloat(guestPaid.guestChannelFee);
        guestSideChannelFeeItems = guestSideChannelFeeItems.filter((item) => Math.abs(parseFloat(item.amount) - chAmount) > 0.01);
      }
      // Airbnb iCal / legacy import: exclude from "Channel remits" badge (badge for official only)
      const rawChannel = String(persistedChannel?.raw_channel ?? canonical?.raw_channel ?? booking?.channel ?? detailData?.reservation?.channel ?? '').toLowerCase();
      const isAirbnbIcal = (persistedChannel?.channel_subtype === 'ical' || canonical?.channel_subtype === 'ical') || rawChannel.includes('airbnbical');
      const isAirbnbOfficialForBadge = isAirbnb && !isAirbnbIcal;
      const isBookingEngine = checkBookingEngine(canonical);
      const isCLC = checkCLC(canonical);
      const isVrbo = persistedChannel?.master_channel_key === 'vrbo' || canonical?.master_channel_key === 'vrbo';
      const processingFees = persistedSummary?.processing_fees;
      // ALIGNMENT FIX: Prefer hostaway_payout_processing_fees (used in payout calculation) over total_processing_fees (Stripe-computed)
      const hostawayPayoutFeeCheck = processingFees?.hostaway_payout_processing_fees;
      const displayProcessingFeesCheck = hostawayPayoutFeeCheck !== null && hostawayPayoutFeeCheck !== undefined && hostawayPayoutFeeCheck > 0
        ? hostawayPayoutFeeCheck
        : parseFloat(processingFees?.total_processing_fees || 0);
      // Guest side: only CLC non-bookingengine shows Processing Fees row. bookingengine Stripe fee is Owner-side only.
      const hasProcessingFees = (isCLC && !isBookingEngine) && processingFees && displayProcessingFeesCheck > 0;
      // Airbnb only: resolution amounts from raw finance (open + closed) so breakdown reconciles to total_paid
      const openRes = parseFloat(detailData?.finance?.airbnb_open_resolutions_sum ?? 0) || 0;
      const closedRes = parseFloat(detailData?.finance?.airbnb_closed_resolutions_sum ?? 0) || 0;
      const resolutionAdjustment = isAirbnb ? (openRes + closedRes) : 0;
      const hasResolutionAdjustment = isAirbnb && Math.abs(resolutionAdjustment) > 0.005;
      const addOnFees = guestPaid.addOnFees || [];
      const hasAddOnFees = addOnFees.length > 0;

      const hasAnyBreakdown = hasBaseRate || hasCleaningFee || hasTouristTax || hasGuestDiscounts || hasGuestServiceFee || hasGuestChannelFee || hasProcessingFees || hasResolutionAdjustment || hasAddOnFees;
      
      if (!hasAnyBreakdown) return null;
      
      return (
        <>
          {guestPaid.totalCollected !== null && (
            <div style={{
              ...styles.folioColumnDividerThick,
              backgroundColor: theme === 'dark' 
                ? 'rgba(255, 255, 255, 0.12)' 
                : 'rgba(200, 195, 188, 0.25)', // Thicker rule above breakdown
              marginTop: '12px', // Tighter spacing (compressed from 20px)
              marginBottom: '12px', // Tighter spacing (compressed from 16px)
            }} />
          )}
          <div style={{
            ...styles.folioColumnBreakdownTitleSecondary,
            color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          }}>
            Breakdown
          </div>
          <div style={styles.folioColumnLedger}>
          {guestPaid.baseRate !== null && (
            <>
              <div style={styles.folioColumnLedgerRowCompressed}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}>
                  <span style={{
                    ...styles.folioColumnLedgerLabelSecondary,
                    color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                  }}>Base Rate</span>
                  {/* GOLD STANDARD: price-per-night = base_rate / nights only. Do NOT include cleaning, taxes, channel/service fees. All channels. */}
                  {(() => {
                    const nights = Math.floor(Number(booking?.nights ?? booking?.nights_count ?? 0)) || 0;
                    const baseRateValue = typeof guestPaid.baseRate === 'number' ? guestPaid.baseRate : parseFloat(guestPaid.baseRate || 0);
                    const nightlyRate = nights > 0 && baseRateValue > 0 ? Math.round((baseRateValue / nights) * 100) / 100 : null;
                    if (nightlyRate !== null && Number.isFinite(nightlyRate) && nights > 0) {
                      return (
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 'var(--font-weight-normal)',
                          color: theme === 'dark' ? 'var(--color-text-tertiary-dark)' : 'var(--color-text-tertiary)',
                          fontStyle: 'italic',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}>
                          {`$${nightlyRate.toFixed(2)} × ${nights} night${nights === 1 ? '' : 's'}`}
                          <span
                            style={styles.folioInfoIcon}
                            onMouseEnter={(e) => {
                              showTooltip(e, 'Nightly rate is calculated from the base rate only. Cleaning fees, taxes, and other fees are not included.');
                            }}
                            onMouseLeave={hideTooltip}
                            onFocus={(e) => {
                              showTooltip(e, 'Nightly rate is calculated from the base rate only. Cleaning fees, taxes, and other fees are not included.');
                            }}
                            onBlur={hideTooltip}
                            tabIndex={0}
                            role="button"
                            aria-label="Nightly rate explanation"
                          >
                            ℹ️
                          </span>
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
                <span style={{
                  ...styles.folioColumnLedgerValueSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  {formatFinanceValue(guestPaid.baseRate)}
                </span>
              </div>
              {(guestPaid.cleaningFee !== null || guestPaid.touristTax !== null || hasAddOnFees || hasGuestDiscounts) && (
                <div style={{
                  ...styles.folioColumnDivider,
                  backgroundColor: theme === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(200, 195, 188, 0.15)',
                }} />
              )}
            </>
          )}
          {guestDiscountLineItems.map((item, index) => (
            <React.Fragment key={`guest-discount-${item.key}`}>
              <div style={styles.folioColumnLedgerRowCompressed}>
                <span style={{
                  ...styles.folioColumnLedgerLabelSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>{item.label}</span>
                <span style={{
                  ...styles.folioColumnLedgerValueSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  {formatFinanceValue(item.amount)}
                </span>
              </div>
              {(index < guestDiscountLineItems.length - 1 || guestPaid.cleaningFee !== null || guestPaid.touristTax !== null || hasAddOnFees) && (
                <div style={{
                  ...styles.folioColumnDivider,
                  backgroundColor: theme === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(200, 195, 188, 0.15)',
                }} />
              )}
            </React.Fragment>
          ))}
          {guestPaid.cleaningFee !== null && (
            <>
              <div style={styles.folioColumnLedgerRowCompressed}>
                <span style={{
                  ...styles.folioColumnLedgerLabelSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>Cleaning Fee</span>
                <span style={{
                  ...styles.folioColumnLedgerValueSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  {formatFinanceValue(guestPaid.cleaningFee)}
                </span>
              </div>
              {(hasAddOnFees || guestPaid.touristTax !== null) && (
                <div style={{
                  ...styles.folioColumnDivider,
                  backgroundColor: theme === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(200, 195, 188, 0.15)',
                }} />
              )}
            </>
          )}
          {hasAddOnFees && addOnFees.map(({ key, label, amount }) => (
            <div key={key} style={styles.folioColumnLedgerRowCompressed}>
              <span style={{
                ...styles.folioColumnLedgerLabelSecondary,
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
              }}>{label}</span>
              <span style={{
                ...styles.folioColumnLedgerValueSecondary,
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
              }}>
                {formatFinanceValue(amount)}
              </span>
            </div>
          ))}
          {hasAddOnFees && guestPaid.touristTax !== null && (
            <div style={{
              ...styles.folioColumnDivider,
              backgroundColor: theme === 'dark' 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'rgba(200, 195, 188, 0.15)',
            }} />
          )}
          {guestPaid.touristTax !== null && (
            <div style={styles.folioColumnLedgerRowCompressed}>
              <span style={{
                ...styles.folioColumnLedgerLabelSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
              }}>
                Tourist Tax
                {(isLegacyVRBOiCal || isAirbnbOfficialForBadge) && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 'var(--font-weight-normal)',
                    color: theme === 'dark' ? 'var(--color-text-tertiary-dark)' : 'var(--color-text-tertiary)',
                    fontStyle: 'italic',
                    padding: '2px 6px',
                    backgroundColor: theme === 'dark' ? 'rgba(131, 189, 193, 0.15)' : 'rgba(131, 189, 193, 0.1)',
                    borderRadius: '4px',
                    marginLeft: '2px',
                  }}>
                    Channel remits
                  </span>
                )}
                <span
                  style={styles.folioInfoIcon}
                  onMouseEnter={(e) => {
                    const tooltipText = isLegacyVRBOiCal
                      ? VRBO_ICAL_TAX_TOOLTIP
                      : (() => {
                          const parts = [];
                          if (hasStateTaxInTooltip) parts.push(`State Tax: ${formatFinanceValue(guestPaid.stateTaxAmount)}`);
                          if (hasCountyTaxInTooltip) parts.push(`County Tax: ${formatFinanceValue(guestPaid.countyTaxAmount)}`);
                          if (parts.length > 0) return parts.join('\n');
                          const canonical = resolveCanonical(detailData, booking);
                          const masterChannelKey = canonical?.master_channel_key;
                          return (masterChannelKey === 'vrbo' || masterChannelKey === 'clc')
                            ? 'Taxes collected and remitted by Owner'
                            : 'Taxes collected and remitted by channel (where applicable). Not paid to owner.';
                        })();
                    showTooltip(e, tooltipText);
                  }}
                  onMouseLeave={hideTooltip}
                  onFocus={(e) => {
                    const tooltipText = isLegacyVRBOiCal
                      ? VRBO_ICAL_TAX_TOOLTIP
                      : (() => {
                          const parts = [];
                          if (hasStateTaxInTooltip) parts.push(`State Tax: ${formatFinanceValue(guestPaid.stateTaxAmount)}`);
                          if (hasCountyTaxInTooltip) parts.push(`County Tax: ${formatFinanceValue(guestPaid.countyTaxAmount)}`);
                          if (parts.length > 0) return parts.join('\n');
                          const canonical = resolveCanonical(detailData, booking);
                          const masterChannelKey = canonical?.master_channel_key;
                          return (masterChannelKey === 'vrbo' || masterChannelKey === 'clc')
                            ? 'Taxes collected and remitted by Owner'
                            : 'Taxes collected and remitted by channel (where applicable). Not paid to owner.';
                        })();
                    showTooltip(e, tooltipText);
                  }}
                  onBlur={hideTooltip}
                  tabIndex={0}
                  role="button"
                  aria-label="Tourist tax breakdown"
                >
                  ℹ️
                </span>
              </span>
              <span style={{
                ...styles.folioColumnLedgerValueSecondary,
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
              }}>
                {formatFinanceValue(guestPaid.touristTax)}
              </span>
            </div>
          )}
          {/* Guest Service Fee: For Airbnb, show guest_channel_fee as "Guest Service Fee". For other channels, show guest_service_fee if present. */}
          {isAirbnb && hasGuestChannelFee ? (
            <>
              {(guestPaid.touristTax !== null || guestPaid.cleaningFee !== null || guestPaid.baseRate !== null) && (
                <div style={{
                  ...styles.folioColumnDivider,
                  backgroundColor: theme === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(200, 195, 188, 0.15)',
                }} />
              )}
              <div style={styles.folioColumnLedgerRowCompressed}>
                <span style={{
                  ...styles.folioColumnLedgerLabelSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  Guest Service Fee
                  <span
                    style={styles.folioInfoIcon}
                    onMouseEnter={(e) => {
                      showTooltip(e, 'Previously paid by the guest to Airbnb. As of Nov 15, Airbnb now charges only the host a 15.5% fee. These fees will only appear for past bookings or any made before Nov 15ish');
                    }}
                    onMouseLeave={hideTooltip}
                    onFocus={(e) => {
                      showTooltip(e, 'Previously paid by the guest to Airbnb. As of Nov 15, Airbnb now charges only the host a 15.5% fee. These fees will only appear for past bookings or any made before Nov 15ish');
                    }}
                    onBlur={hideTooltip}
                    tabIndex={0}
                    role="button"
                    aria-label="Guest Service Fee explanation"
                  >
                    ℹ️
                  </span>
                </span>
                <span style={{
                  ...styles.folioColumnLedgerValueSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  {formatFinanceValue(guestPaid.guestChannelFee)}
                </span>
              </div>
            </>
          ) : hasGuestServiceFee ? (
            <>
              {(guestPaid.touristTax !== null || guestPaid.cleaningFee !== null || guestPaid.baseRate !== null) && (
                <div style={{
                  ...styles.folioColumnDivider,
                  backgroundColor: theme === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(200, 195, 188, 0.15)',
                }} />
              )}
              <div style={styles.folioColumnLedgerRowCompressed}>
                <span style={{
                  ...styles.folioColumnLedgerLabelSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>Guest Service Fee</span>
                <span style={{
                  ...styles.folioColumnLedgerValueSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  {formatFinanceValue(guestPaid.guestServiceFee)}
                </span>
              </div>
            </>
          ) : null}
          {/* Channel Fees (guest-paid): single label + Guest badge only */}
          {guestSideChannelFeeItems.length > 0 && guestSideChannelFeeItems.map((item, idx) => (
            <React.Fragment key={`guest-cf-${idx}`}>
              {(guestPaid.touristTax !== null || guestPaid.cleaningFee !== null || guestPaid.baseRate !== null || hasGuestServiceFee || idx > 0) && (
                <div style={{
                  ...styles.folioColumnDivider,
                  backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(200, 195, 188, 0.15)',
                }} />
              )}
              <div style={styles.folioColumnLedgerRowCompressed}>
                <span style={{
                  ...styles.folioColumnLedgerLabelSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  Channel Fees
                  <ChannelFeeBadge who="guest" theme={theme} />
                  <span
                    style={styles.folioInfoIcon}
                    onMouseEnter={(e) => { showTooltip(e, channelFeesTooltipWithInfoLink(BOOKINGENGINE_GUEST_CHANNEL_FEE_TOOLTIP)); }}
                    onMouseLeave={hideTooltip}
                    onFocus={(e) => { showTooltip(e, channelFeesTooltipWithInfoLink(BOOKINGENGINE_GUEST_CHANNEL_FEE_TOOLTIP)); }}
                    onBlur={hideTooltip}
                    tabIndex={0}
                    role="button"
                    aria-label="Channel fee explanation"
                  >
                    ℹ️
                  </span>
                </span>
                <span style={{
                  ...styles.folioColumnLedgerValueSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  {formatFinanceValue(item.amount)}
                </span>
              </div>
            </React.Fragment>
          ))}
          {/* Resolution Adjustment (Airbnb only): open + closed resolutions so breakdown sums to Paid by Guest */}
          {hasResolutionAdjustment && (
            <>
              {(guestPaid.touristTax !== null || guestPaid.cleaningFee !== null || guestPaid.baseRate !== null || hasGuestServiceFee || guestSideChannelFeeItems.length > 0) && (
                <div style={{
                  ...styles.folioColumnDivider,
                  backgroundColor: theme === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(200, 195, 188, 0.15)',
                }} />
              )}
              <div style={styles.folioColumnLedgerRowCompressed}>
                <span style={{
                  ...styles.folioColumnLedgerLabelSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>Resolution Adjustment</span>
                <span style={{
                  ...styles.folioColumnLedgerValueSecondary,
                  color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                }}>
                  {formatFinanceValue(resolutionAdjustment)}
                </span>
              </div>
            </>
          )}
          {/* Crissy: bookingengine Stripe fee is Owner-side only; do NOT show on Guest Payment. CLC non-bookingengine keeps "Processing Fees" row. */}
          {(() => {
            const canonicalInner = resolveCanonical(detailData, booking);
            const isBookingEngineInner = checkBookingEngine(canonicalInner);
            const isCLCInner = checkCLC(canonicalInner);
            const processingFees = persistedSummary?.processing_fees;
            const hostawayPayoutFee = processingFees?.hostaway_payout_processing_fees;
            const displayProcessingFees = hostawayPayoutFee !== null && hostawayPayoutFee !== undefined && hostawayPayoutFee > 0
              ? hostawayPayoutFee
              : parseFloat(processingFees?.total_processing_fees || 0);
            if (!processingFees || displayProcessingFees <= 0) return null;
            const totalProcessingFees = displayProcessingFees;

            if (isBookingEngineInner) return null;

            if (isCLCInner) {
              let tooltipText = 'Processing fees deducted from payout (Hostaway payment_service_processing_fees).';
              if (hostawayPayoutFee != null && hostawayPayoutFee > 0) {
                tooltipText = `Processing fees used in payout calculation: $${totalProcessingFees.toFixed(2)} (from Hostaway).`;
              } else if (processingFees.processing_fee_breakdown) {
                const breakdown = typeof processingFees.processing_fee_breakdown === 'string'
                  ? JSON.parse(processingFees.processing_fee_breakdown)
                  : processingFees.processing_fee_breakdown;
                if (breakdown.stripe_fee_total != null && breakdown.hostaway_fee_total != null) {
                  tooltipText = `Processing fees: Stripe $${parseFloat(breakdown.stripe_fee_total).toFixed(2)} + Hostaway $${parseFloat(breakdown.hostaway_fee_total).toFixed(2)} = $${totalProcessingFees.toFixed(2)}`;
                  if (breakdown.charge_count) tooltipText += ` (${breakdown.charge_count} charge${breakdown.charge_count > 1 ? 's' : ''})`;
                }
              }
              return (
                <>
                  {guestPaid.touristTax !== null && (
                    <div style={{
                      ...styles.folioColumnDivider,
                      backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(200, 195, 188, 0.15)',
                    }} />
                  )}
                  <div style={styles.folioColumnLedgerRowCompressed}>
                    <span style={{
                      ...styles.folioColumnLedgerLabelSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                    }}>
                      Processing Fees
                      <span
                        style={styles.folioInfoIcon}
                        onMouseEnter={(e) => { showTooltip(e, tooltipText); }}
                        onMouseLeave={hideTooltip}
                        onFocus={(e) => { showTooltip(e, tooltipText); }}
                        onBlur={hideTooltip}
                        tabIndex={0}
                        role="button"
                        aria-label="Processing fees explanation"
                      >
                        ℹ️
                      </span>
                    </span>
                    <span style={{
                      ...styles.folioColumnLedgerValueSecondary,
                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                    }}>
                      {formatFinanceValue(totalProcessingFees)}
                    </span>
                  </div>
                </>
              );
            }
            return null;
          })()}
        </div>
      </>
      );
    })()}
    </div>
  </div>
</div>
  );
}
